import { get, post, put, del } from '../client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Gallery,
  GalleryFolderResponse,
  GalleryDetail,
  UpdateGallery,
  UpdateGalleryStatus,
  GalleryStats
} from '../types'

export const galleryService = {
  // Create a new gallery with photo URLs
  createGallery: async (data: {
    folderId: string;
    title: string;
    photoUrls: string[];
    isKeyHighlight: boolean;
    isMultimedia: boolean;
    status: 'DRAFT' | 'PUBLISH';
  }): Promise<ApiResponse<Gallery>> => {
    return await post<ApiResponse<Gallery>>(
      'galleries/admin/private',
      data
    )
  },

  // Update an existing gallery
  updateGallery: async (
    galleryId: string,
    data: UpdateGallery
  ): Promise<ApiResponse<Gallery>> => {
    return await put<ApiResponse<Gallery>>(
      `galleries/admin/private/${galleryId}`,
      data
    )
  },

  // Delete a gallery
  deleteGallery: async (galleryId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `galleries/admin/private/${galleryId}`
    )
  },

  // Delete a photo from gallery
  deletePhoto: async (photoId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `galleries/admin/private/removephoto/${photoId}`
    )
  },

  // Update gallery status
  updateGalleryStatus: async (
    galleryId: string,
    data: UpdateGalleryStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `galleries/admin/private/${galleryId}/status`,
      data
    )
  },

  // Add photos to gallery
  addPhotoToGallery: async (
    galleryId: string,
    photoUrls: string[]
  ): Promise<ApiResponse<null>> => {
    return await post<ApiResponse<null>>(
      `galleries/admin/private/addphoto/${galleryId}`,
      { photoUrls }
    )
  },

  // Filter galleries with pagination (admin)
  filterGalleries: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: Record<string, any> = {}
  ): Promise<ApiResponse<PaginatedResponse<GalleryFolderResponse>>> => {
    return await post<ApiResponse<PaginatedResponse<GalleryFolderResponse>>>(
      `galleries/admin/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get galleries (public endpoint with pagination)
  getGalleries: async (
    params: PaginationParams = { page: 1, size: 10 }
  ): Promise<ApiResponse<GalleryFolderResponse[]>> => {
    return await get<ApiResponse<GalleryFolderResponse[]>>(
      `galleries/private?size=${params.size}&page=${params.page}`
    )
  },

  // Get gallery details by ID
  getGalleryById: async (galleryId: string): Promise<ApiResponse<GalleryDetail>> => {
    return await get<ApiResponse<GalleryDetail>>(
      `galleries/private/gallery/${galleryId}`
    )
  },

  // Get folder details (galleries grouped by folder)
  getFolderDetails: async (
    folderId: string
  ): Promise<ApiResponse<GalleryFolderResponse>> => {
    return await get<ApiResponse<GalleryFolderResponse>>(
      `galleries/private/folder/${folderId}`
    )
  },

  // Get total gallery stats
  getTotalStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'galleries/private/stats/total'
    )
  },

  // Get published gallery stats
  getPublishedStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'galleries/private/stats/published'
    )
  },

  // Get draft gallery stats
  getDraftStats: async (): Promise<ApiResponse<number>> => {
    return await get<ApiResponse<number>>(
      'galleries/private/stats/draft'
    )
  },

  // Get all gallery stats (combined)
  getGalleryStats: async (): Promise<ApiResponse<GalleryStats>> => {
    const [totalResponse, publishedResponse, draftResponse] = await Promise.all([
      galleryService.getTotalStats(),
      galleryService.getPublishedStats(),
      galleryService.getDraftStats()
    ])

    const stats: GalleryStats = {
      total: totalResponse.data || 0,
      published: publishedResponse.data || 0,
      draft: draftResponse.data || 0
    }

    return {
      success: true,
      message: 'Gallery stats retrieved successfully',
      data: stats
    }
  }
}
